import React, { useState } from 'react';
import { Input, Button, Tag, message } from 'antd';
import { GiftOutlined, CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';

const CouponInput = ({
  isCouponApplied,
  couponCode,
  couponBonusAmount,
  applyingCoupon,
  removingCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  isDarkMode,
  disabled = false,
}) => {
  const [inputCode, setInputCode] = useState('');
  const [localError, setLocalError] = useState('');

  const handleApply = async () => {
    if (!inputCode.trim()) {
      setLocalError('Please enter a coupon code');
      return;
    }

    setLocalError('');
    const result = await onApplyCoupon(inputCode.trim());
    
    if (result?.success === false) {
      setLocalError(result.message || 'Invalid coupon code');
    } else {
      setInputCode('');
      message.success('Coupon applied successfully!');
    }
  };

  const handleRemove = async () => {
    const result = await onRemoveCoupon();
    if (result?.success !== false) {
      setInputCode('');
      message.success('Coupon removed successfully');
    }
  };

  if (isCouponApplied) {
    return (
      <div className={`p-4 rounded-lg border-2 border-green-500 ${isDarkMode ? 'bg-gray-800' : 'bg-green-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircleOutlined className="text-xl text-green-500" />
            <div>
              <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Coupon Applied!
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Tag color="success" className="font-mono">
                  {couponCode}
                </Tag>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  +{couponBonusAmount > 0 ? `₹${couponBonusAmount}` : '0'} bonus
                </span>
              </div>
            </div>
          </div>
          <Button
            type="text"
            danger
            icon={<CloseCircleOutlined />}
            onClick={handleRemove}
            loading={removingCoupon}
            disabled={disabled || removingCoupon}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <Input
            placeholder="Enter coupon code"
            value={inputCode}
            onChange={(e) => {
              setInputCode(e.target.value.toUpperCase());
              setLocalError('');
            }}
            onPressEnter={handleApply}
            prefix={<GiftOutlined className="text-gray-400" />}
            disabled={disabled || applyingCoupon}
            size="large"
            className="rounded-lg"
            status={localError ? 'error' : ''}
          />
          {localError && (
            <div className="mt-1 text-xs text-red-500">{localError}</div>
          )}
        </div>
        <Button
          type="primary"
          onClick={handleApply}
          loading={applyingCoupon}
          disabled={disabled || applyingCoupon || !inputCode.trim()}
          className="h-10 px-6 border-none bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600"
        >
          Apply
        </Button>
      </div>
      <div className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        <GiftOutlined className="mr-1" />
        Enter a valid coupon code to get additional bonus
      </div>
    </div>
  );
};

export default CouponInput;